// Scene object registry. Tracks Three.js objects with their physical-space
// positions so a coordinate-system rotation can replay the physical→Three.js
// mapping for every registered entry. Two groups by convention: 'physics' for
// scene-graph objects (spheres, etc.) and 'axes' for coordinate axes/labels.
//
// `mapPhysicalToThreeWithRotation` is injected so the module stays unaware of
// `core/coordinates.ts` — the dep makes the rotation source explicit.

import type * as THREE from 'three';

interface RegistryEntry {
    id: string;
    name: string;
    type: string;
    group: string;
    threeObject: THREE.Object3D;
    physicalPosition: THREE.Vector3 | null;
}

interface RegisterArgs {
    id?: string;
    name: string;
    type: string;
    group: string;
    threeObject: THREE.Object3D;
    position?: THREE.Vector3 | { x: number; y: number; z: number } | null;
}

export interface RegistryListEntry {
    id: string;
    name: string;
    type: string;
    threeObject: THREE.Object3D;
}

export interface ObjectRegistry {
    register: (args: RegisterArgs) => string;
    unregister: (id: string, group: string) => void;
    setPhysicalPositionById: (id: string, group: string, position: THREE.Vector3) => void;
    updateAllTransforms: () => void;
    generateId: (prefix?: string) => string;
    listGroup: (group: string) => RegistryListEntry[];
}

interface ObjectRegistryDeps {
    mapPhysicalToThreeWithRotation: (vec: THREE.Vector3 | { x: number; y: number; z: number }) => THREE.Vector3;
}

function toVector3(v: THREE.Vector3 | { x: number; y: number; z: number } | null | undefined): THREE.Vector3 | null {
    if (!v) return null;
    // Duck-typed: caller always passes a real THREE.Vector3 in current call sites.
    return (v as THREE.Vector3).clone();
}

export function createObjectRegistry({ mapPhysicalToThreeWithRotation }: ObjectRegistryDeps): ObjectRegistry {
    const groups: Record<string, Map<string, RegistryEntry>> = {
        physics: new Map(),
        axes: new Map(),
    };
    let nextId = 1;

    function generateId(prefix: string = 'obj'): string {
        return `${prefix}_${nextId++}`;
    }

    function entryFromThree(threeObj: THREE.Object3D | null | undefined): RegistryEntry | null {
        const ud = threeObj && threeObj.userData as { registryGroup?: string; registryId?: string };
        if (!ud) return null;
        const reg = ud.registryGroup ? groups[ud.registryGroup] : null;
        return (reg && ud.registryId) ? (reg.get(ud.registryId) || null) : null;
    }

    function updateTransform(entryOrThree: RegistryEntry | THREE.Object3D): void {
        const entry: RegistryEntry | null = (entryOrThree as RegistryEntry).threeObject
            ? (entryOrThree as RegistryEntry)
            : entryFromThree(entryOrThree as THREE.Object3D);
        if (!entry || !entry.threeObject) return;
        const obj = entry.threeObject;
        const physical = obj.userData['physicalPosition'] as THREE.Vector3 | undefined;
        if (!physical) return;
        obj.position.copy(mapPhysicalToThreeWithRotation(physical));
    }

    function register(args: RegisterArgs): string {
        const id = args.id || generateId(args.type || args.group || 'obj');
        const physicalPosition = toVector3(args.position ?? null);
        const entry: RegistryEntry = { id, name: args.name, type: args.type, group: args.group, threeObject: args.threeObject, physicalPosition };
        args.threeObject.userData['registryId'] = id;
        args.threeObject.userData['registryGroup'] = args.group;
        if (entry.physicalPosition) {
            args.threeObject.userData['physicalPosition'] = entry.physicalPosition.clone();
            updateTransform(entry);
        }
        if (!groups[args.group]) groups[args.group] = new Map<string, RegistryEntry>();
        groups[args.group]!.set(id, entry);
        return id;
    }

    function unregister(id: string, group: string): void {
        const reg = groups[group];
        if (!reg) return;
        const entry = reg.get(id);
        if (entry) {
            if (entry.threeObject && entry.threeObject.parent) {
                entry.threeObject.parent.remove(entry.threeObject);
            }
            reg.delete(id);
        }
    }

    function setPhysicalPositionById(id: string, group: string, position: THREE.Vector3): void {
        const reg = groups[group];
        if (!reg) return;
        const entry = reg.get(id);
        if (!entry || !entry.threeObject) return;
        entry.physicalPosition = position.clone();
        entry.threeObject.userData['physicalPosition'] = entry.physicalPosition.clone();
        updateTransform(entry);
    }

    function updateAllTransforms(): void {
        (['physics', 'axes'] as const).forEach((group) => {
            const reg = groups[group];
            if (!reg) return;
            reg.forEach((entry) => updateTransform(entry));
        });
    }

    function listGroup(group: string): RegistryListEntry[] {
        const reg = groups[group];
        if (!reg) return [];
        const out: RegistryListEntry[] = [];
        reg.forEach((entry) => {
            out.push({ id: entry.id, name: entry.name, type: entry.type, threeObject: entry.threeObject });
        });
        return out;
    }

    return { register, unregister, setPhysicalPositionById, updateAllTransforms, generateId, listGroup };
}
